<template>
  <ContextColorProvider context="trade">
    <LoaderIndicator
      v-if="isLoading"
      class="profit-unsubscribe__loader"
      with-container
    />
    <HelperViewStatus
      v-else-if="hasError"
      class="profit-unsubscribe__error"
      title="Houve um problema ao carregar as informações."
      description="Aguarde um momento e tente novamente"
      status="alert"
      button-label="Tentar novamente"
      @click="fetchProfitCancelMenu"
    />
    <div v-else-if="profitCancelMenu">
      <SelectionStep
        v-if="step === Steps.SELECT_PLAN"
        page-title=""
        :aria-label="profitCancelMenu?.cancellation.title"
        :title="profitCancelMenu?.cancellation.title"
        :head="profitCancelMenu?.cancellation.header"
        :options="mountOptions"
        :value="profitOptionSelected"
        :disabled="profitOptionSelected === ''"
        @change="handleSelectOption"
        @submit="setStep(Steps.CONFIRM)"
        @back="backToProfitSubscription"
      />
      <ReviewStep
        v-else-if="step === Steps.CONFIRM"
        :head="planSelected?.value"
        :title="profitCancelMenu?.confirmation.title"
        :subtitle="profitCancelMenu?.confirmation.description"
        @back="backToProfitSubscription"
      >
        <template #button-container>
          <ButtonContainer>
            <template #button-primary>
              <ButtonPrimary
                :label="profitCancelMenu?.confirmation.confirmButton"
                @click="setStep(Steps.SELECT_REASON)"
              />
            </template>
            <template #button-secondary>
              <ButtonSecondary
                :label="profitCancelMenu?.confirmation.leaveButton"
                @click="backToProfitSubscription"
              />
            </template>
          </ButtonContainer>
        </template>
      </ReviewStep>
      <SelectionStep
        v-else-if="isReasonStep"
        page-title=""
        :aria-label="profitCancelMenu?.reasons.title"
        :title="profitCancelMenu?.reasons.title"
        :head="planSelected?.value"
        :options="mountReasons"
        :value="profitReasonSelected"
        :disabled="profitReasonSelected === ''"
        @back="setStep(Steps.CONFIRM)"
        @change="handleSelectReason"
        @submit="handleSubmitReason"
      />
      <Modal
        v-if="step === Steps.DESCRIBE_REASON"
        :is-open="isModalReasonOpen"
        size="md"
        @close="closeModalReason"
      >
        <div class="profit-unsubscribe__modal-content">
          <Heading :level="3">Descreva o motivo</Heading>
          <TextBox
            v-model="reasonDescription"
            acessible-label="Campo de texto para descrever o motivo do cancelamento do profit"
            @clear="clearInputReason"
          />
        </div>
        <template #buttonContainer>
          <ButtonContainer>
            <template #button-primary>
              <ButtonPrimary
                label="Confirmar"
                size="large"
                :loading="isLoadingCancelPlan"
                @click="proceedUnsubscribe"
              />
            </template>
            <template #button-secondary>
              <ButtonSecondary
                label="Cancelar"
                size="large"
                @click="closeModalReason"
              />
            </template>
          </ButtonContainer>
        </template>
      </Modal>
    </div>
  </ContextColorProvider>
</template>

<script setup lang="ts">
import {
  SelectionStep,
  ReviewStep,
  ButtonContainer,
  ButtonPrimary,
  ButtonSecondary,
  Modal,
  Heading,
  ContextColorProvider,
  LoaderIndicator,
  HelperViewStatus
} from '@warrenbrasil/nebraska-web';
import { computed, onMounted, ref } from 'vue';
import {
  getProfitCancelMenu,
  cancelProfit
} from '@/modules/trade/services/profit';
import type { ProfitCancelMenu } from '@/modules/trade/services/profit';
import { useRouter } from 'vue-router/composables';
import { notify } from '@/modules/common/helpers/notification';
import { NotificationTypes } from '@/modules/common/services/http/notification';
import { isBFFError } from '@/modules/common/helpers/bff';
import { TextBox } from '@/modules/common/components/TextBox';

enum Steps {
  SELECT_PLAN = 'select-plan',
  CONFIRM = 'confirm',
  SELECT_REASON = 'select-reason',
  DESCRIBE_REASON = 'describe-reason'
}

const profitCancelMenu = ref<ProfitCancelMenu | null>(null);
const isLoading = ref(false);
const hasError = ref(false);
const profitOptionSelected = ref<string | number>('');
const profitReasonSelected = ref<string | number>('');
const step = ref<Steps | null>(null);
const isModalReasonOpen = ref(false);
const reasonDescription = ref('');
const isLoadingCancelPlan = ref(false);

const router = useRouter();

const mountOptions = computed(() => {
  return profitCancelMenu.value?.cancellation.options.map(option => ({
    title: option.value,
    value: option.id
  }));
});

const mountReasons = computed(() => {
  return profitCancelMenu.value?.reasons.reasonsOptions.map(
    (reason, index) => ({
      title: reason.value,
      value: index
    })
  );
});

const planSelected = computed(() => {
  return profitCancelMenu.value?.cancellation.options.find(
    option => option.id === profitOptionSelected.value
  );
});

const reasonSelected = computed(() => {
  return profitCancelMenu.value?.reasons.reasonsOptions[
    profitReasonSelected.value! as number
  ];
});

const hasMultiplePlans = computed(() => {
  return (
    profitCancelMenu.value?.cancellation &&
    profitCancelMenu.value?.cancellation.options.length > 1
  );
});

const isReasonStep = computed(() => {
  return (
    step.value === Steps.SELECT_REASON || step.value === Steps.DESCRIBE_REASON
  );
});

async function fetchProfitCancelMenu() {
  isLoading.value = true;

  try {
    profitCancelMenu.value = await getProfitCancelMenu();
    setFirstStep();
  } catch (error) {
    hasError.value = true;
  } finally {
    isLoading.value = false;
  }
}

function handleSelectOption(value: number) {
  profitOptionSelected.value = value;
}

function handleSelectReason(value: number) {
  profitReasonSelected.value = value;
}

function clearInputReason() {
  reasonDescription.value = '';
}

function handleSubmitReason() {
  if (reasonSelected.value?.shouldOpenTextEditor) {
    isModalReasonOpen.value = true;
    setStep(Steps.DESCRIBE_REASON);
  } else {
    proceedUnsubscribe();
  }
}

function backToProfitSubscription() {
  if (step.value === Steps.CONFIRM && hasMultiplePlans.value) {
    setStep(Steps.SELECT_PLAN);
  } else {
    router.push({ name: 'profitSubscription' });
  }
}

async function proceedUnsubscribe() {
  isLoadingCancelPlan.value = true;
  try {
    const cancelPlan = await cancelProfit(
      planSelected.value!.id,
      reasonDescription.value || reasonSelected.value?.value!
    );

    notify({
      group: 'wrn-notifications',
      text: cancelPlan.message,
      type: NotificationTypes.success
    });

    backToProfitSubscription();
  } catch (e) {
    getErrorMessage(e);
  } finally {
    isLoadingCancelPlan.value = false;
  }
}

function setStep(stepToGo: Steps | null) {
  step.value = stepToGo;
}

function closeModalReason() {
  isModalReasonOpen.value = false;
}

function setFirstStep() {
  if (hasMultiplePlans.value) {
    step.value = Steps.SELECT_PLAN;
  } else {
    profitOptionSelected.value =
      profitCancelMenu.value!.cancellation.options[0].id;
    step.value = Steps.CONFIRM;
  }
}

function getErrorMessage(e: unknown) {
  let errorMessage = 'Houve um problema ao cancelar o Profit.';
  if (isBFFError(e)) {
    errorMessage = e.error.message.formatted;
  }
  notify({
    group: 'wrn-notifications',
    text: errorMessage,
    type: NotificationTypes.error
  });
}

onMounted(async () => {
  await fetchProfitCancelMenu();
});
</script>

<style scoped lang="less">
.profit-unsubscribe {
  &__loader {
    height: 100vh;
  }

  &__error {
    height: 100vh;
    margin: auto;
    justify-content: center;
  }

  &__modal-content {
    width: 100%;
    text-align: left;
  }
}
</style>
